import React from 'react';
import './InvestmentPoolCard.css';

const InvestmentPoolCard = ({ 
  pool, 
  userInvestment, 
  onInvest, 
  onWithdraw 
}) => {
  const formatNumber = (num) => {
    const numValue = parseFloat(num);
    if (isNaN(numValue) || numValue === 0) return '0.000';
    if (numValue >= 1e6) return `${(numValue / 1e6).toFixed(3)}M`;
    if (numValue >= 1e3) return `${(numValue / 1e3).toFixed(3)}K`;
    return numValue.toFixed(3);
  };

  const formatPercentage = (percent) => {
    const percentValue = parseFloat(percent);
    if (isNaN(percentValue) || percentValue === 0) return '0.00%';
    const formatted = percentValue.toFixed(2);
    return `${percentValue >= 0 ? '+' : ''}${formatted}%`;
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low': return '#28a745';
      case 'medium': return '#ffc107';
      case 'high': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'paused': return '🟡';
      case 'closed': return '🔴';
      default: return '⚪';
    }
  };

  // Calculate user investment return if they have an investment
  const userReturn = userInvestment ? 
    ((userInvestment.current_value - userInvestment.amount_invested) / userInvestment.amount_invested * 100) : 0;

  return (
    <div className="investment-pool-card">
      <div className="pool-header">
        <div className="pool-info">
          <h4 className="pool-name">{pool.name}</h4>
          <p className="pool-description">{pool.description}</p>
          <div className="pool-manager">
            <span className="manager-label">Managed by:</span>
            <span className="manager-name">{pool.manager_name}</span>
            {pool.manager_verified && (
              <span className="verified-badge" title="Verified Manager">✓</span>
            )}
          </div>
        </div>
        <div className={`pool-status ${pool.status}`}>
          {getStatusIcon(pool.status)} {pool.status}
        </div>
      </div>

      <div className="pool-metrics">
        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Total Pool Size</span>
            <span className="metric-value">{formatNumber(pool.total_size)} SOL</span>
          </div>
          <div className="metric">
            <span className="metric-label">Available</span>
            <span className="metric-value">{formatNumber(pool.available_size)} SOL</span>
          </div>
        </div>
        
        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">30d Return</span>
            <span className={`metric-value ${pool.return_30d >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(pool.return_30d || 0)}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">All-time Return</span>
            <span className={`metric-value ${pool.total_return >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(pool.total_return || 0)}
            </span>
          </div>
        </div>

        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Management Fee</span>
            <span className="metric-value">{pool.management_fee}% annual</span>
          </div>
          <div className="metric">
            <span className="metric-label">Performance Fee</span>
            <span className="metric-value">{pool.performance_fee}% of profits</span>
          </div>
        </div>

        <div className="metrics-row">
          <div className="metric">
            <span className="metric-label">Min Investment</span>
            <span className="metric-value">{formatNumber(pool.min_investment)} SOL</span>
          </div>
          <div className="metric">
            <span className="metric-label">Max Investment</span>
            <span className="metric-value">
              {pool.max_investment ? `${formatNumber(pool.max_investment)} SOL` : 'No limit'}
            </span>
          </div>
        </div>
      </div>

      <div className="pool-strategy">
        <h5>Investment Strategy</h5>
        <p className="strategy-description">{pool.strategy}</p>
        <div className="risk-level">
          <span className="risk-label">Risk Level:</span>
          <span 
            className="risk-value"
            style={{ color: getRiskLevelColor(pool.risk_level) }}
          >
            {pool.risk_level?.toUpperCase() || 'UNKNOWN'}
          </span>
        </div>
      </div>

      {pool.withdrawal_notice_days > 0 && (
        <div className="withdrawal-notice">
          <span className="notice-icon">ℹ️</span>
          <span className="notice-text">
            {pool.withdrawal_notice_days} day notice required for withdrawals
          </span>
        </div>
      )}

      {userInvestment && (
        <div className="user-investment-info">
          <h5>Your Investment</h5>
          <div className="investment-metrics">
            <div className="investment-metric">
              <span className="metric-label">Invested Amount</span>
              <span className="metric-value">{formatNumber(userInvestment.amount_invested)} SOL</span>
            </div>
            <div className="investment-metric">
              <span className="metric-label">Current Value</span>
              <span className="metric-value">{formatNumber(userInvestment.current_value)} SOL</span>
            </div>
            <div className="investment-metric">
              <span className="metric-label">Your Return</span>
              <span className={`metric-value ${userReturn >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(userReturn)}
              </span>
            </div>
            <div className="investment-metric">
              <span className="metric-label">Shares Owned</span>
              <span className="metric-value">{formatNumber(userInvestment.shares)}</span>
            </div>
          </div>
          
          {userInvestment.fees_paid > 0 && (
            <div className="fees-info">
              <span className="fees-label">Total Fees Paid:</span>
              <span className="fees-value">{formatNumber(userInvestment.fees_paid)} SOL</span>
            </div>
          )}
        </div>
      )}

      <div className="pool-actions">
        <button 
          className="invest-btn primary"
          onClick={onInvest}
          disabled={pool.status !== 'active'}
        >
          {userInvestment ? 'Add More' : 'Invest'}
        </button>
        
        {userInvestment && userInvestment.status === 'active' && (
          <button 
            className="withdraw-btn secondary"
            onClick={onWithdraw}
            disabled={pool.withdrawal_locked}
          >
            {pool.withdrawal_locked ? 'Withdrawals Locked' : 'Withdraw'}
          </button>
        )}
        
        <button className="details-btn tertiary">
          View Details
        </button>
      </div>

      {pool.status !== 'active' && (
        <div className="pool-notice">
          {pool.status === 'paused' && (
            <span className="notice-text">⏸️ Pool is temporarily paused</span>
          )}
          {pool.status === 'closed' && (
            <span className="notice-text">🔒 Pool is closed to new investments</span>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestmentPoolCard;